import { Injectable } from '@angular/core';
import {
  ComparisonOperation,
  DateOperation,
  FunctionOperation,
  LogicalOperation,
  MathematicalOperation,
  TextOperation
} from '@models/helpers/formula-operation';
import { FormulaField, FormulaValidationError, FormulaValidationResult } from '@module-definition/formulas/cipo-formula/formula.model';
import { TranslateService } from '@ngx-translate/core';
import { formulaToJsonlogic } from 'jsonlogic-formula';

@Injectable({
  providedIn: 'root',
})
export class FormulaValidationService {
  constructor(private translate: TranslateService) {}

  // Centralized metadata for parameter counts
  private static OPERATION_METADATA: Record<string, { minParams: number }> = {
    not: { minParams: 1 },
    isnull: { minParams: 1 },
    isnotnull: { minParams: 1 },
    isempty: { minParams: 1 },
    isnotempty: { minParams: 1 },
    between: { minParams: 3 },
    dateadd: { minParams: 3 },
    datediff: { minParams: 3 },
  };

  private static getAllOperators(): string[] {
    return [
      ...Object.keys(MathematicalOperation),
      ...Object.keys(LogicalOperation),
      ...Object.keys(ComparisonOperation),
      ...Object.keys(TextOperation),
      ...Object.keys(DateOperation),
      ...Object.keys(FunctionOperation),
    ]
      .filter(key => isNaN(Number(key)))
      .map(op => op.toLowerCase());
  }

  private static getMinParams(op: string): number {
    return FormulaValidationService.OPERATION_METADATA[op]?.minParams ?? 2;
  }

  private normalize(val: string): string {
    return val.trim().toLowerCase();
  }

  validateFormula(
    formula: string,
    fieldOptions: FormulaField[],
  ): FormulaValidationResult {
    if (!formula?.trim()) {
      return this.errorResult('formulas.errors.enterFormulaFirst');
    }

    // Preprocess NOT operator first
    formula = this.preprocessNot(formula);

    //  Structure validation
    const structureError = this.validateFormulaStructure(formula, fieldOptions);
    if (structureError) return this.wrapError(structureError);

    //  Parentheses validation
    const parenthesesError = this.validateParentheses(formula);
    if (parenthesesError) return this.wrapError(parenthesesError);

    // Check for multiple expressions before field preprocessing
    // But exclude known field names from this check
    const fieldNames = fieldOptions
      .map(opt => ((opt as any).label ? (opt as any).label : opt.label.split(' - ')[0].replace(/^\[.*?\]\s*/, '')))
      .sort((a, b) => b.length - a.length);

    // Create a temporary formula with field names replaced for validation
    let tempFormula = formula;
    const quotedStrings: string[] = [];

    // Preserve quoted strings
    tempFormula = tempFormula.replace(/"([^"]*)"/g, match => {
      quotedStrings.push(match);
      return `__QUOTE_${quotedStrings.length - 1}__`;
    });

    // Replace known field names with placeholders
    fieldNames.forEach((field, index) => {
      const escapedField = field.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`\\b${escapedField.replace(/\s+/g, '\\s+')}\\b`, 'gi');
      tempFormula = tempFormula.replace(regex, `__FIELD_${index}__`);
    });

    // Now check for multiple expressions in the cleaned formula
    const multipleExprPattern = /[A-Za-z_][A-Za-z0-9_]*\s+[A-Za-z_][A-Za-z0-9_]*/;
    const multipleExprMatch = multipleExprPattern.exec(tempFormula);
    if (multipleExprMatch) {
      return this.errorResult(
        'formulas.errors.multipleTopLevelExpressions',
        multipleExprMatch[0],
        multipleExprMatch.index,
        multipleExprMatch.index + multipleExprMatch[0].length,
      );
    }

    //  Field extraction
    const availableFieldNames = fieldOptions.map(opt =>
      (opt as any).name ? (opt as any).name : opt.label.split(' - ')[0].replace(/^\[.*?\]\s*/, ''),
    );
    const usedFields = this.extractUsedFields(formula, availableFieldNames);

    //  Invalid fields
    const invalidFields = this.extractInvalidFields(formula, availableFieldNames);
    if (invalidFields.length > 0) {
      const firstInvalid = invalidFields[0];
      const identifierPattern = /\b([A-Za-z][A-Za-z0-9 _]*)\b/g;
      let match: RegExpExecArray | null;
      let start = -1,
        end = -1;
      while ((match = identifierPattern.exec(formula)) !== null) {
        if (match[1].trim() === firstInvalid) {
          start = match.index;
          end = start + match[1].length;
          break;
        }
      }
      return this.errorResult('formulas.errors.invalidFields', firstInvalid, start, end, {
        fields: invalidFields.join(', '),
      });
    }

    // Now preprocess field names properly
    formula = this.preprocessFormulaFields(formula, fieldOptions);

    //  Operator validation (after preprocessing)
    const allowedMultiCharOps = ['<=', '>=', '<>'];
    const invalidOpPattern = /([!+\-*/<>=]{2,})/g;
    let match;
    while ((match = invalidOpPattern.exec(formula)) !== null) {
      const op = match[0].trim();
      if (!allowedMultiCharOps.includes(op)) {
        return this.errorResult(
          'formulas.errors.invalidOperator',
          match[0],
          match.index,
          match.index + match[0].length,
          { operator: op },
        );
      }
    }

    //  Start/end parenthesis validation
    const endsWithStartParenError = this.validateEndsWithStartParenthesis(formula);
    if (endsWithStartParenError) return this.wrapError(endsWithStartParenError);

    //  Field references validation
    const fieldReferencesError = this.validateFieldReferences(formula);
    if (fieldReferencesError) return this.wrapError(fieldReferencesError);

    //  Syntax structure validation
    const syntaxStructureError = this.validateSyntaxStructure(formula);
    if (syntaxStructureError) return this.wrapError(syntaxStructureError);

    //  Operator context validation
    const operatorContextError = this.validateOperatorContext(formula);
    if (operatorContextError) return this.wrapError(operatorContextError);

    //  Numeric values validation
    const numericValuesError = this.validateNumericValues(formula);
    if (numericValuesError) return this.wrapError(numericValuesError);

    //  Function existence validation
    const functionsError = this.validateFunctions(formula);
    if (functionsError) return this.wrapError(functionsError);

    //  Function parameter count validation
    const functionParamCountError = this.validateFunctionParameterCount(formula);
    if (functionParamCountError) return this.wrapError(functionParamCountError);

    //  JSON Logic conversion
    let jsonLogicObj: any;
    try {
      const preprocessedFormula = this.preprocessFormulaFields(formula, fieldOptions);
      jsonLogicObj = formulaToJsonlogic(preprocessedFormula);
      jsonLogicObj = this.fixFieldReferences(jsonLogicObj, fieldOptions);
    } catch (error) {
      return this.errorResult('formulas.errors.invalidJsonLogic', formula);
    }

    const usedFieldIds = fieldOptions
      .filter(opt =>
        usedFields.includes(
          (opt as any).name ? (opt as any).name : opt.label.split(' - ')[0].replace(/^\[.*?\]\s*/, ''),
        ),
      )
      .map(opt => Number(opt.id));

    return { isValid: true, errors: [], usedFieldIds, jsonLogicObj };
  }

  // Utility to wrap a single error
  private wrapError(error: FormulaValidationError): FormulaValidationResult {
    return { isValid: false, errors: [error], usedFieldIds: [] };
  }

  // Utility to create error result
  private errorResult(
    key: string,
    highlightText?: string,
    startPosition?: number,
    endPosition?: number,
    params?: any,
  ): FormulaValidationResult {
    return {
      isValid: false,
      errors: [
        {
          message: this.translate.instant(key, params),
          startPosition,
          endPosition,
          highlightText,
        },
      ],
      usedFieldIds: [],
    };
  }

  // Preprocesses NOT operator
  private preprocessNot(formula: string): string {
    return formula.replace(/!([a-zA-Z0-9_\[\] ]+|\([^\)]+\))/g, (match, expr) => `not(${expr.trim()})`);
  }

  // Preprocesses field names in formula
  private preprocessFormulaFields(formula: string, fieldOptions: FormulaField[]): string {
    const fieldNames = fieldOptions
      .map(opt => ((opt as any).name ? (opt as any).name : opt.label.split(' - ')[0].replace(/^\[.*?\]\s*/, '')))
      .sort((a, b) => b.length - a.length); 

    let processedFormula = formula;
    const quotedStrings: string[] = [];
    const bracketedFields: string[] = [];

    processedFormula = processedFormula.replace(/"([^"]*)"/g, match => {
      quotedStrings.push(match);
      return `__QUOTE_${quotedStrings.length - 1}__`;
    });

    processedFormula = processedFormula.replace(/\[([^\]]+)\]/g, match => {
      bracketedFields.push(match);
      return `__BRACKET_${bracketedFields.length - 1}__`;
    });

    fieldNames.forEach(field => {
      const escapedField = field.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(?<![\\w\\d])${escapedField.replace(/\s+/g, '\\s+')}(?![\\w\\d])`, 'gi');
      processedFormula = processedFormula.replace(regex, `[${field}]`);
    });

    processedFormula = processedFormula.replace(/__BRACKET_(\d+)__/g, (_, idx) => bracketedFields[Number(idx)]);

    processedFormula = processedFormula.replace(/__QUOTE_(\d+)__/g, (_, idx) => quotedStrings[Number(idx)]);

    return processedFormula;
  }

  // Extracts used fields from formula
  private extractUsedFields(formula: string, availableFieldNames: string[]): string[] {
    const identifierPattern = /(?<![\w\d])([A-Za-z][A-Za-z0-9 _]*)(?![\w\d])/g;
    let match;
    const usedFields: string[] = [];
    const normalizedAvailableFieldNames = availableFieldNames.map(this.normalize);
    const allOperatorsLower = FormulaValidationService.getAllOperators();

    let formulaForFields = formula.replace(/"[^"]*"/g, ' ');
    while ((match = identifierPattern.exec(formulaForFields)) !== null) {
      const candidate = match[1].trim();
      if (
        !allOperatorsLower.includes(this.normalize(candidate)) &&
        isNaN(Number(candidate)) &&
        candidate.length > 0 &&
        normalizedAvailableFieldNames.includes(this.normalize(candidate))
      ) {
        usedFields.push(availableFieldNames[normalizedAvailableFieldNames.indexOf(this.normalize(candidate))]);
      }
    }
    return usedFields;
  }

  // Extracts invalid fields from formula
  private extractInvalidFields(formula: string, availableFieldNames: string[]): string[] {
    const identifierPattern = /(?<![\w\d])([A-Za-z][A-Za-z0-9 _]*)(?![\w\d])/g;
    let match;
    const invalidFields: string[] = [];
    const normalizedAvailableFieldNames = availableFieldNames.map(this.normalize);
    const allOperatorsLower = FormulaValidationService.getAllOperators();

    let formulaForFields = formula.replace(/"[^"]*"/g, ' ');
    while ((match = identifierPattern.exec(formulaForFields)) !== null) {
      const candidate = match[1].trim();
      if (
        !allOperatorsLower.includes(this.normalize(candidate)) &&
        isNaN(Number(candidate)) &&
        candidate.length > 0 &&
        !normalizedAvailableFieldNames.includes(this.normalize(candidate))
      ) {
        invalidFields.push(candidate);
      }
    }
    return invalidFields;
  }

  // Validates that the formula is not just a single value or field, but a real formula.
  private validateFormulaStructure(formula: string, fieldOptions?: FormulaField[]): FormulaValidationError | null {
    const trimmed = formula.trim();

    if (/^[-+]?\d+(\.\d+)?$/.test(trimmed)) {
      return {
        message: this.translate.instant('formulas.errors.notAFormula'),
        startPosition: 0,
        endPosition: trimmed.length,
        highlightText: trimmed,
      };
    }

    if (/^"[^"]+"$/.test(trimmed)) {
      return {
        message: this.translate.instant('formulas.errors.notAFormula'),
        startPosition: 0,
        endPosition: trimmed.length,
        highlightText: trimmed,
      };
    }

    if (/^\[[^\]]+\]$/.test(trimmed)) {
      return {
        message: this.translate.instant('formulas.errors.notAFormula'),
        startPosition: 0,
        endPosition: trimmed.length,
        highlightText: trimmed,
      };
    }

    if (/\[\s*\[[^\]]+\]\s*\]/.test(trimmed)) {
      return {
        message: this.translate.instant('formulas.errors.notAFormula'),
        startPosition: 0,
        endPosition: trimmed.length,
        highlightText: trimmed,
      };
    }

    if (/^(\d+\s+)+\d+$/.test(trimmed)) {
      return {
        message: this.translate.instant('formulas.errors.notAFormula'),
        startPosition: 0,
        endPosition: trimmed.length,
        highlightText: trimmed,
      };
    }

    if (fieldOptions) {
      const availableFieldNames = fieldOptions.map(opt =>
        (opt as any).name ? (opt as any).name : opt.label.split(' - ')[0].replace(/^\[.*?\]\s*/, ''),
      );
      const normalize = (val: string) => val.trim().toLowerCase();

      const hasOperators =
        /[+\-*/=<>!]|\b(and|or|not|between|isnull|isnotnull|isempty|isnotempty|dateadd|datediff)\b/i.test(trimmed);

      if (!hasOperators && availableFieldNames.some(name => normalize(name) === normalize(trimmed))) {
        return {
          message: this.translate.instant('formulas.errors.notAFormula'),
          startPosition: 0,
          endPosition: trimmed.length,
          highlightText: trimmed,
        };
      }
    }

    return null;
  }

  // Validates that all parentheses in the formula are balanced and correctly placed.
  private validateParentheses(formula: string): FormulaValidationError | null {
    const openStack: number[] = [];
    for (let i = 0; i < formula.length; i++) {
      const char = formula[i];
      if (char === '(') {
        openStack.push(i);
      } else if (char === ')') {
        if (openStack.length > 0) {
          openStack.pop();
        } else {
          // Unmatched closing parenthesis
          return {
            message: this.translate.instant('formulas.errors.unexpectedClosingParenthesis', { position: i + 1 }),
            startPosition: i,
            endPosition: i + 1,
            highlightText: ')',
          };
        }
      }
    }
    if (openStack.length > 0) {
      // Highlight the first unmatched '('
      const firstUnmatchedOpen = openStack[0];
      return {
        message: this.translate.instant('formulas.errors.parenthesesMismatch', {
          count: openStack.length,
          plural: openStack.length > 1 ? 'es' : '',
          position: firstUnmatchedOpen + 1,
        }),
        startPosition: firstUnmatchedOpen,
        endPosition: firstUnmatchedOpen + 1,
        highlightText: '(',
      };
    }
    return null;
  }

  // Validates that the formula does not end with an opening parenthesis.
  private validateEndsWithStartParenthesis(formula: string): FormulaValidationError | null {
    const trimmed = formula.trim();
    if (trimmed.endsWith('(')) {
      return {
        message: this.translate.instant('formulas.errors.endsWithStartParenthesis'),
        startPosition: trimmed.length - 1,
        endPosition: trimmed.length,
        highlightText: '(',
      };
    }
    return null;
  }

  // Validates that operators are not at the start or end of the formula, and not consecutive.
  private validateOperatorContext(formula: string): FormulaValidationError | null {
    const allOperators = FormulaValidationService.getAllOperators();
    const trimmed = formula.trim();

    const startOpPattern = new RegExp(`^([+\\-*/]|${allOperators.join('|')})(?!\\s*\\()`, 'i');
    const startOpMatch = startOpPattern.exec(trimmed);
    if (startOpMatch) {
      return {
        message: this.translate.instant('formulas.errors.operatorAtBoundary'),
        startPosition: 0,
        endPosition: startOpMatch[0].length,
        highlightText: startOpMatch[0],
      };
    }

    const endOpPattern = new RegExp(`([+\\-*/]|${allOperators.join('|')})$`, 'i');
    const endOpMatch = endOpPattern.exec(trimmed);
    if (endOpMatch) {
      return {
        message: this.translate.instant('formulas.errors.operatorAtBoundary'),
        startPosition: trimmed.length - endOpMatch[0].length,
        endPosition: trimmed.length,
        highlightText: endOpMatch[0],
      };
    }

    const consecutiveOperatorPattern = new RegExp(
      `\\b(${allOperators.join('|')})\\s+(${allOperators.join('|')})\\b(?!\\s*\\()`,
      'gi',
    );
    const consecutiveMatch = consecutiveOperatorPattern.exec(formula);
    if (consecutiveMatch) {
      return {
        message: this.translate.instant('formulas.errors.consecutiveOperators'),
        startPosition: consecutiveMatch.index,
        endPosition: consecutiveMatch.index + consecutiveMatch[0].length,
        highlightText: consecutiveMatch[0],
      };
    }
    return null;
  }

  //Validates that field references are correctly quoted and do not contain invalid characters.
  private validateFieldReferences(formula: string): FormulaValidationError | null {
    let inQuotes = false;
    let quoteStart = -1;
    for (let i = 0; i < formula.length; i++) {
      if (formula[i] === '"') {
        if (!inQuotes) {
          inQuotes = true;
          quoteStart = i;
        } else {
          inQuotes = false;
          quoteStart = -1;
        }
      }
    }
    if (inQuotes && quoteStart !== -1) {
      return {
        message: this.translate.instant('formulas.errors.unclosedQuotes'),
        startPosition: quoteStart,
        endPosition: quoteStart + 1,
        highlightText: '"',
      };
    }
    const quotedStrings = formula.match(/"[^"]*"/g);
    if (quotedStrings) {
      for (const quotedString of quotedStrings) {
        const fieldName = quotedString.slice(1, -1);
        const invalidCharsPattern = /[<>|&%$#@!^*()+={}\\\/~`]/;
        const invalidMatch = invalidCharsPattern.exec(fieldName);
        if (invalidMatch) {
          const quotedIndex = formula.indexOf(quotedString);
          const invalidCharIndex = quotedIndex + 1 + fieldName.indexOf(invalidMatch[0]);
          return {
            message: this.translate.instant('formulas.errors.invalidCharactersInFields', { fields: quotedString }),
            startPosition: invalidCharIndex,
            endPosition: invalidCharIndex + invalidMatch[0].length,
            highlightText: invalidMatch[0],
          };
        }
      }
    }
    return null;
  }

  // Validates the overall syntax structure of the formula for common mistakes.
  private validateSyntaxStructure(formula: string): FormulaValidationError | null {
    const mathOps = Object.keys(MathematicalOperation).filter(key => isNaN(Number(key)));
    const logicalOps = Object.keys(LogicalOperation).filter(key => isNaN(Number(key)));
    const compOps = Object.keys(ComparisonOperation).filter(key => isNaN(Number(key)));
    const textOps = Object.keys(TextOperation).filter(key => isNaN(Number(key)));
    const dateOps = Object.keys(DateOperation).filter(key => isNaN(Number(key)));
    const binaryOps = [...mathOps, ...logicalOps.filter(op => op !== 'Not'), ...compOps, ...textOps, ...dateOps];

    const multipleTopLevelExprPattern = /\)\s*[A-Za-z_][A-Za-z0-9_]*(\s*\(|\b)/;
    const multiExprMatch = multipleTopLevelExprPattern.exec(formula);
    if (multiExprMatch) {
      return {
        message: this.translate.instant('formulas.errors.multipleTopLevelExpressions'),
        startPosition: multiExprMatch.index,
        endPosition: multiExprMatch.index + multiExprMatch[0].length,
        highlightText: multiExprMatch[0],
      };
    }

    const missingOperandPattern = new RegExp(
      `\\(\\s*(${binaryOps.join('|')})\\s*\\)|(${binaryOps.join('|')})\\s*\\)`,
      'gi',
    );
    const missingOperandMatch = missingOperandPattern.exec(formula);
    if (missingOperandMatch) {
      return {
        message: this.translate.instant('formulas.errors.missingOperands'),
        startPosition: missingOperandMatch.index,
        endPosition: missingOperandMatch.index + missingOperandMatch[0].length,
        highlightText: missingOperandMatch[0],
      };
    }

    const emptyParensMatch = /\(\s*\)/.exec(formula);
    if (emptyParensMatch) {
      return {
        message: this.translate.instant('formulas.errors.emptyParentheses'),
        startPosition: emptyParensMatch.index,
        endPosition: emptyParensMatch.index + emptyParensMatch[0].length,
        highlightText: emptyParensMatch[0],
      };
    }

    // Look for true multiple expressions - at this point field names should be bracketed
    // Pattern: number/closing-paren/closing-bracket/quote followed by space and then letter/opening-bracket/number
    // that is NOT part of a bracketed field reference
    const adjacentExprPattern = /(\d+(?:\.\d+)?|\)|\]|\")\s+(\[|[A-Za-z]|\d)/;
    const adjacentExprMatch = adjacentExprPattern.exec(formula);
    if (adjacentExprMatch) {
      return {
        message: this.translate.instant('formulas.errors.multipleTopLevelExpressions'),
        startPosition: adjacentExprMatch.index,
        endPosition: adjacentExprMatch.index + adjacentExprMatch[0].length,
        highlightText: adjacentExprMatch[0],
      };
    }
    return null;
  }

  //Validates that numeric values in the formula are well-formed and checks for division by zero.
  private validateNumericValues(formula: string): FormulaValidationError | null {
    const invalidNumberPattern = /\b\d+\.\d*\.\d*\b|\b\d*\.\.\d*\b/g;
    const invalidNumberMatch = invalidNumberPattern.exec(formula);
    if (invalidNumberMatch) {
      return {
        message: this.translate.instant('formulas.errors.invalidNumbers', { numbers: invalidNumberMatch[0] }),
        startPosition: invalidNumberMatch.index,
        endPosition: invalidNumberMatch.index + invalidNumberMatch[0].length,
        highlightText: invalidNumberMatch[0],
      };
    }
    const divideByZeroPattern = /\bDivide\s*\([^,)]*,\s*0\s*\)/gi;
    const divideByZeroMatch = divideByZeroPattern.exec(formula);
    if (divideByZeroMatch) {
      const zeroMatch = /,\s*0\s*\)/.exec(divideByZeroMatch[0]);
      if (zeroMatch) {
        const zeroStartInMatch = divideByZeroMatch[0].indexOf(zeroMatch[0]);
        const zeroIndex = zeroMatch[0].indexOf('0');
        const absoluteZeroPos = divideByZeroMatch.index + zeroStartInMatch + zeroIndex;
        return {
          message: this.translate.instant('formulas.errors.divisionByZero'),
          startPosition: absoluteZeroPos,
          endPosition: absoluteZeroPos + 1,
          highlightText: '0',
        };
      }
    }

    const infixDivideByZeroPattern = /\/\s*0(?![\d\.])/g;
    let infixMatch;
    while ((infixMatch = infixDivideByZeroPattern.exec(formula)) !== null) {
      const zeroIndex = infixDivideByZeroPattern.lastIndex - 1;
      return {
        message: this.translate.instant('formulas.errors.divisionByZero'),
        startPosition: zeroIndex,
        endPosition: zeroIndex + 1,
        highlightText: '0',
      };
    }

    // Additional checks for division by zero with expressions
    const divideExpressionPattern = /\bDivide\s*\(([^,]+),\s*(.+)\)$/gi;
    let divideExpressionMatch;
    while ((divideExpressionMatch = divideExpressionPattern.exec(formula)) !== null) {
      const divisorExpression = divideExpressionMatch[2].trim();
      const subtractPattern = /^Subtract\s*\(\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)\s*\)$/i;
      const subtractMatch = subtractPattern.exec(divisorExpression);
      if (subtractMatch) {
        const num1 = parseFloat(subtractMatch[1]);
        const num2 = parseFloat(subtractMatch[2]);
        if (num1 === num2) {
          const divisorStart = divideExpressionMatch.index + divideExpressionMatch[0].indexOf(divisorExpression);
          return {
            message: this.translate.instant('formulas.errors.divisionByZero'),
            startPosition: divisorStart,
            endPosition: divisorStart + divisorExpression.length,
            highlightText: divisorExpression,
          };
        }
      }
      const addPattern = /^Add\s*\(\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*\)$/i;
      const addMatch = addPattern.exec(divisorExpression);
      if (addMatch) {
        const num1 = parseFloat(addMatch[1]);
        const num2 = parseFloat(addMatch[2]);
        if (num1 + num2 === 0) {
          const divisorStart = divideExpressionMatch.index + divideExpressionMatch[0].indexOf(divisorExpression);
          return {
            message: this.translate.instant('formulas.errors.divisionByZero'),
            startPosition: divisorStart,
            endPosition: divisorStart + divisorExpression.length,
            highlightText: divisorExpression,
          };
        }
      }
      const multiplyZeroPatterns = [/^Multiply\s*\([^,)]*,\s*0\s*\)$/i, /^Multiply\s*\(\s*0\s*,[^)]*\)$/i];
      for (const pattern of multiplyZeroPatterns) {
        if (pattern.test(divisorExpression)) {
          const divisorStart = divideExpressionMatch.index + divideExpressionMatch[0].indexOf(divisorExpression);
          return {
            message: this.translate.instant('formulas.errors.divisionByZero'),
            startPosition: divisorStart,
            endPosition: divisorStart + divisorExpression.length,
            highlightText: divisorExpression,
          };
        }
      }
    }
    return null;
  }

  //Validates that all function names in the formula are known/allowed.
  private validateFunctions(formula: string): FormulaValidationError | null {
    const allValidOperators = new Set(FormulaValidationService.getAllOperators());
    const functionPattern = /\b([A-Za-z][A-Za-z0-9]*)\s*\(/g;
    let functionMatch;
    while ((functionMatch = functionPattern.exec(formula)) !== null) {
      const functionName = functionMatch[1];
      if (!allValidOperators.has(functionName.toLowerCase())) {
        return {
          message: this.translate.instant('formulas.errors.unknownFunction', { function: functionName }),
          startPosition: functionMatch.index,
          endPosition: functionMatch.index + functionName.length,
          highlightText: functionName,
        };
      }
    }
    return null;
  }

  // Recursively validates that all function calls have the correct number of parameters.
  private validateFunctionParameterCount(formula: string): FormulaValidationError | null {
    const checkFunctions = (expr: string, offset = 0): FormulaValidationError | null => {
      let i = 0;
      while (i < expr.length) {
        if (/[A-Za-z_]/.test(expr[i])) {
          let start = i;
          while (i < expr.length && /[A-Za-z0-9_]/.test(expr[i])) i++;
          const name = expr.slice(start, i);
          while (i < expr.length && /\s/.test(expr[i])) i++;
          if (expr[i] === '(') {
            let paramsStart = i + 1;
            let depth = 1;
            let paramsEnd = paramsStart;
            while (paramsEnd < expr.length && depth > 0) {
              if (expr[paramsEnd] === '(') depth++;
              else if (expr[paramsEnd] === ')') depth--;
              paramsEnd++;
            }
            if (depth === 0) {
              const paramsStr = expr.slice(paramsStart, paramsEnd - 1);
              const params: string[] = [];
              let param = '';
              let nested = 0;
              for (let j = 0; j < paramsStr.length; j++) {
                if (paramsStr[j] === '(') nested++;
                if (paramsStr[j] === ')') nested--;
                if (paramsStr[j] === ',' && nested === 0) {
                  params.push(param.trim());
                  param = '';
                } else {
                  param += paramsStr[j];
                }
              }
              if (param.trim()) params.push(param.trim());

              const funcName = name.toLowerCase();
              const minCount = FormulaValidationService.getMinParams(funcName);
              if (params.length < minCount) {
                return {
                  message: this.translate.instant('formulas.errors.functionParameterCount', {
                    function: funcName,
                    minCount,
                    params: params.join(', '),
                  }),
                  startPosition: offset + start,
                  endPosition: offset + paramsEnd,
                  highlightText: expr.slice(start, paramsEnd),
                };
              }

              for (const param of params) {
                const nestedError = checkFunctions(param, offset + paramsStart);
                if (nestedError) return nestedError;
              }

              i = paramsEnd;
              continue;
            }
          }
        }
        i++;
      }
      return null;
    };

    return checkFunctions(formula);
  }

  // Recursively fixes field references in the parsed JSON Logic object
  private fixFieldReferences(obj: any, fieldOptions: FormulaField[]): any {
    if (typeof obj !== 'object' || obj === null) {
      if (typeof obj === 'string') {
        const bracketMatch = obj.match(/^\[([^\]]+)\]$/);
        if (bracketMatch) {
          const normalizedField = this.findMatchingAvailableField(bracketMatch[1], fieldOptions);
          return { var: normalizedField ?? bracketMatch[1] };
        }
      }
      return obj;
    }
    if (Array.isArray(obj)) {
      return obj.map(item => this.fixFieldReferences(item, fieldOptions));
    }
    const result: any = {};
    for (const key in obj) {
      const value = obj[key];
      if (Array.isArray(value)) {
        result[key] = value.map(item => this.fixFieldReferences(item, fieldOptions));
      } else {
        result[key] = this.fixFieldReferences(value, fieldOptions);
      }
    }
    return result;
  }

  // Finds a matching field name in the field options, handling various formats
  private findMatchingAvailableField(fieldName: string, fieldOptions: FormulaField[]): string | null {
    if (!fieldName) return null;
    const normalizedInput = this.normalize(fieldName);
    for (const option of fieldOptions) {
      const name = (option as any).name || option.label.split(' - ')[0].replace(/^\[.*?\]\s*/, '');
      if (this.normalize(name) === normalizedInput) {
        return name;
      }
    }
    return null;
  }
}